import { z } from '../zod';

// Response shapes of the dashboard order bump editor routes, as serialized:
// ids and dates arrive as strings.

const OrderBumpEditorShowHideLogicResponseSchema = z.object({
  enabled: z.boolean(),
  subject: z.enum(['PRICE', 'TICKET', 'ORDER_BUMP']),
  comparison: z.enum(['IS', 'IS_NOT']),
  priceIds: z.array(z.string()),
  ticketTypeIds: z.array(z.string()),
  orderBumpProductIds: z.array(z.string()),
  labels: z.array(z.object({ id: z.string(), name: z.string().nullish() })),
});

const OrderBumpEditorMediaSchema = z.looseObject({}).meta({ description: 'A product media item.' });

export const OrderBumpEditorEntryResponseValidationSchema = z.object({
  productId: z.string(),
  addedAt: z.string().nullish(),
  enabled: z.boolean(),
  preselected: z.boolean(),
  hidden: z.boolean(),
  showHideLogic: OrderBumpEditorShowHideLogicResponseSchema,
  allowQuantity: z.boolean(),
  shortenDescription: z.boolean(),
  usedOnPageCount: z
    .number()
    .meta({ description: 'How many of the store pages offer this product as a bump.' }),
  hasPurchases: z
    .boolean()
    .meta({ description: 'Whether the product has been bought, so cannot be deleted.' }),
  isSoldOut: z
    .boolean()
    .meta({ description: 'Whether the product tracks stock and has none left.' }),
  product: z.object({
    _id: z.string(),
    title: z
      .string()
      .meta({ description: 'Product title, or the product ID once the product is deleted.' }),
    currency: z.string().nullish(),
    media: z.array(OrderBumpEditorMediaSchema).meta({ description: 'The first media item only.' }),
  }),
  price: z
    .object({
      amount: z.number(),
      currency: z.string(),
      billingType: z.string(),
      interval: z.string().nullish(),
      intervalCount: z.number().nullish(),
      payWhatYouWant: z
        .boolean()
        .optional()
        .meta({ description: 'Whether the buyer names the amount; `amount` is then the minimum.' }),
      pwywSuggestedPrice: z.number().nullish(),
    })
    .optional()
    .meta({ description: 'The offer price. Absent when the bump is unavailable.' }),
  unavailableReason: z
    .string()
    .optional()
    .meta({ description: 'Why the bump cannot be offered on this page. Absent when it can.' }),
});

export const OrderBumpEditorListResponseValidationSchema = z.object({
  orderBumps: z.array(OrderBumpEditorEntryResponseValidationSchema),
  billingModel: z.enum(['charge', 'subscription', 'mixed']).nullish(),
  orderBumpProducts: z.array(z.looseObject({ _id: z.string() })).meta({
    description: 'The bump products in the storefront projection, for the checkout preview.',
  }),
});

const OrderBumpEditorStatsSchema = z.object({
  sales: z.number(),
  revenue: z.number(),
  payments: z.number(),
  refunded: z.number(),
  lastSoldAt: z.string().nullable(),
  salesSinceAdded: z.number().meta({
    description:
      'Sales on the pages offering the order bump now, since it was added to each: the share of `payments` that included it. `sales` also counts pages it was removed from.',
  }),
});

export const OrderBumpEditorInfoResponseValidationSchema = z.object({
  product: z.object({
    shortDescription: z.string().nullable(),
    sku: z.string().nullable(),
    stock: z.number().nullable().meta({ description: 'Null when stock is unlimited.' }),
    priceReference: z.string().nullable(),
  }),
  pages: z.array(
    z.object({
      _id: z.string(),
      name: z.string().nullish(),
      type: z.string().nullish(),
      imageUrl: z.string().nullable(),
      eventDetails: z
        .object({
          startDate: z.string().nullish(),
          endDate: z.string().nullish(),
          timezone: z.string().nullish(),
        })
        .optional(),
      product: z.looseObject({}).nullable().optional(),
    }),
  ),
  stats: z.object({ thisPage: OrderBumpEditorStatsSchema, allPages: OrderBumpEditorStatsSchema }),
});
